

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaBuilding, FaChevronRight } from "react-icons/fa";

const Cities = React.memo(({ data }) => {
  /* ================= LOGIC (UNCHANGED) ================= */
  const [citiesData, setCitiesData] = useState([]);
  const history = useHistory();

  useEffect(() => {
    if (Array.isArray(data.data) && data.data.length > 0) {
      setCitiesData(data.data);
    } else {
      setCitiesData([{ city_name: "Default City", property_count: 0 }]);
    }
  }, [data]);

  const cities = useMemo(
    () => [
      { name: "Mumbai", imageUrl: "/image/mumbai.jpg", properties: "0" },
      { name: "Delhi", imageUrl: "/image/delhi.jpg", properties: "0" },
      { name: "Bangalore", imageUrl: "/image/banglore.jpg", properties: "0" },
      { name: "Chennai", imageUrl: "/image/chennai.jpg", properties: "0" },
      { name: "Hyderabad", imageUrl: "/image/hyderabad.jpg", properties: "0" },
      { name: "Kolkata", imageUrl: "/image/kolkatta.jpg", properties: "0" },
      { name: "Pune", imageUrl: "/image/pune.jpg", properties: "0" },
      { name: "Ahmedabad", imageUrl: "/image/ahemdabad.jpg", properties: "0" },
    ],
    []
  );

  const updatedCities = useMemo(() => {
    return cities
      .map((city) => {
        const cityData = citiesData.find(
          (apiCity) => apiCity.city_name === city.name
        );
        return {
          ...city,
          propertyCount: cityData ? cityData.property_count : 0,
          properties: cityData ? `${cityData.property_count}+` : city.properties,
        };
      })
      .sort((a, b) => b.propertyCount - a.propertyCount);
  }, [cities, citiesData]);

  const handleCityClick = useCallback(
    (cityName) => {
      history.push({
        pathname: `/citywiseproperties/${encodeURIComponent(cityName)}`,
      });
    },
    [history]
  );
  /* ================= END LOGIC ================= */

  /* ================= HIDE ON SCROLL (UI ONLY) ================= */
  const HIDE_AFTER = 80;
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const onScroll = () => setHidden(window.scrollY > HIDE_AFTER);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 800,
    slidesToShow: 7,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    arrows: true,
    responsive: [
      { breakpoint: 1400, settings: { slidesToShow: 6, slidesToScroll: 1 } },
      { breakpoint: 1024, settings: { slidesToShow: 4, slidesToScroll: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      {
        breakpoint: 480,
        settings: { slidesToShow: 2, slidesToScroll: 1, arrows: false },
      },
    ],
  };

  return (
    <section
      aria-label="Explore Real Estate in Popular Indian Cities"
      className={`fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#5c0a22] via-[#7a0f2e] to-[#5c0a22] shadow-xl transition-transform duration-500 ${
        hidden ? "translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="flex flex-col items-center justify-between gap-1.5 px-3 py-1.5 mx-auto max-w-6xl lg:flex-row lg:gap-3 lg:px-5">
        {/* ---------- Left Title Area (Optimized for SEO/AEO & Readability) ---------- */}
        <div className="text-center lg:text-left shrink-0 lg:w-48">
          <p className="text-[7px] font-bold tracking-[0.18em] text-[#f2b632] uppercase">
            Explore Top Cities
          </p>
          <h2 className="text-xs font-semibold text-white leading-tight">
            Find Opportunities in Major Cities
          </h2>
        </div>

        {/* Divider */}
        <div className="hidden w-px h-6 bg-white/20 lg:block" />

        {/* ---------- City Carousel ---------- */}
        <div className="cities-carousel flex-1 w-full min-w-0 px-2 lg:px-3">
          <Slider {...sliderSettings}>
            {updatedCities.map((city) => (
              <div key={city.name}>
                <button
                  type="button"
                  className="flex flex-col items-center justify-center w-full gap-0 py-0.5 text-white transition bg-transparent border-0 cursor-pointer hover:scale-105 group"
                  onClick={() => handleCityClick(city.name)}
                  title={`${city.properties} properties in ${city.name}`}
                >
                  <FaBuilding size={20} className="text-[#f2b632] transition-transform group-hover:scale-110" />
                  <span className="text-[9px] font-medium tracking-tight mt-0.5">{city.name}</span>
                </button>
              </div>
            ))}
          </Slider>
        </div>

        {/* Gold Action Button */}
        {/* <button
          type="button"
          onClick={() => history.push("/property")}
          className="hidden items-center justify-center w-7 h-7 text-[#5c0a22] bg-[#f2b632] border-0 rounded-full cursor-pointer lg:flex shrink-0 transition-transform hover:scale-105 shadow-md"
          aria-label="View all cities and properties"
        >
          <FaChevronRight size={10} />
        </button> */}
      </div>

      {/* ---------- Compact Custom Carousel & Scrollbar Styles ---------- */}
      <style>{`
        .cities-carousel .slick-slide { padding: 0 3px; }
        .cities-carousel .slick-prev,
        .cities-carousel .slick-next {
          z-index: 10;
          width: 20px;
          height: 20px;
        }
        .cities-carousel .slick-prev { left: -16px; }
        .cities-carousel .slick-next { right: -16px; }
        .cities-carousel .slick-prev::before,
        .cities-carousel .slick-next::before {
          color: #f2b632;
          font-size: 16px;
        }
      `}</style>
    </section>
  );
});

export default Cities;